import {
  IndeterminateProgress,
  ResponsiveContent,
  Tabs,
  TabsContent,
} from "@facade/components/ui";
import { OrganizationTab, validTabs } from "./ViewOrganizationPage.types";
import { Suspense, lazy, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { OrganizationSideMenu } from "./components";
import { ViewOrganizationContext } from "./contexts";
import { usePageTitle } from "@facade/hooks";

const loadMembersView = () =>
  import("./views/MembersView").then((module) => ({
    default: module.MembersView,
  }));

const loadGeneralView = () =>
  import("./views/GeneralView").then((module) => ({
    default: module.GeneralView,
  }));

const loadInvitesView = () =>
  import("./views/InvitesView").then((module) => ({
    default: module.InvitesView,
  }));

const MembersView = lazy(loadMembersView);
const GeneralView = lazy(loadGeneralView);
const InvitesView = lazy(loadInvitesView);

const OrganizationTabContent = ({
  activeTab,
}: {
  activeTab: OrganizationTab;
}) => {
  switch (activeTab) {
    case "general":
      return <GeneralView />;

    case "members":
      return <MembersView />;

    case "invites":
      return <InvitesView />;

    default:
      return null;
  }
};

const ViewOrganizationPage = () => {
  const navigate = useNavigate();
  const { organizationId, tab } = useParams();
  usePageTitle("Organization");

  if (!organizationId) {
    throw new Error();
  }

  const activeTab: OrganizationTab = validTabs.includes(tab as OrganizationTab)
    ? (tab as OrganizationTab)
    : "general";

  useEffect(() => {
    if (!tab || !validTabs.includes(tab as OrganizationTab)) {
      navigate(`/organization/${organizationId}/general`, { replace: true });
    }
  }, [tab, navigate, organizationId]);

  return (
    <ViewOrganizationContext.Provider
      value={{
        id: organizationId,
      }}
    >
      <ResponsiveContent reduced>
        <Tabs
          orientation="vertical"
          value={activeTab}
          className="gap-6 w-full flex-1 overflow-hidden p-4 pt-0"
          onValueChange={(value) => {
            navigate(`/organization/${organizationId}/${value}`);
          }}
        >
          <OrganizationSideMenu />
          <TabsContent value={activeTab} className="flex min-h-full">
            <Suspense key={activeTab} fallback={<IndeterminateProgress />}>
              <OrganizationTabContent activeTab={activeTab} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </ResponsiveContent>
    </ViewOrganizationContext.Provider>
  );
};

export default ViewOrganizationPage;
